import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { TrendingUp, Upload, AlertCircle, CheckCircle, DollarSign, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { div7aAPI } from '../lib/div7aAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { formatAUDate } from '../lib/dateUtils';

export default function Div7AScanner() {
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  
  // Manual transaction entry
  const [manualEntry, setManualEntry] = useState({
    client_id: '',
    transaction_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    account_code: '',
    account_name: '',
    transaction_type: 'debit',
    party_name: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [transactionsRes, clientsRes] = await Promise.all([
        div7aAPI.listTransactions({ flagged_only: false }),
        clientsAPI.list()
      ]);
      setTransactions(transactionsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error('Failed to load transactions');
      console.error(error);
    }
  };

  const handleManualScan = async (e) => {
    e.preventDefault();
    
    if (!manualEntry.client_id || !manualEntry.amount || !manualEntry.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await div7aAPI.scanTransactions([
        {
          client_id: manualEntry.client_id,
          transaction_date: manualEntry.transaction_date,
          amount: parseFloat(manualEntry.amount),
          description: manualEntry.description,
          account_code: manualEntry.account_code || undefined,
          account_name: manualEntry.account_name || undefined,
          transaction_type: manualEntry.transaction_type,
          party_name: manualEntry.party_name || undefined
        }
      ]);
      
      toast.success(`Scanned ${response.data.scanned_count} transaction(s). ${response.data.flagged_count} flagged for Division 7A.`);
      setShowUploadDialog(false);
      resetManualEntry();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan transaction');
    } finally {
      setLoading(false);
    }
  };

  const resetManualEntry = () => {
    setManualEntry({
      client_id: '',
      transaction_date: new Date().toISOString().split('T')[0],
      amount: '',
      description: '',
      account_code: '',
      account_name: '',
      transaction_type: 'debit',
      party_name: ''
    });
  };

  const handleClassify = async (transactionId, classification) => {
    try {
      await div7aAPI.classifyTransaction(transactionId, classification);
      toast.success('Transaction reclassified');
      loadData();
    } catch (error) {
      toast.error('Failed to classify transaction');
    }
  };

  const handleDecision = async (decision) => {
    if (!selectedTransaction) return;
    
    try {
      await div7aAPI.recordDecision(selectedTransaction.id, decision);
      toast.success(`Decision recorded: ${decision}`);
      setShowDecisionDialog(false);
      setSelectedTransaction(null);
      loadData();
    } catch (error) {
      toast.error('Failed to record decision');
    }
  };

  const openDecisionDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDecisionDialog(true);
  };

  const getClassificationColor = (classification) => {
    const colors = {
      loan: 'bg-red-100 text-red-800 border-red-200',
      salary: 'bg-green-100 text-green-800 border-green-200',
      dividend: 'bg-blue-100 text-blue-800 border-blue-200',
      reimbursement: 'bg-purple-100 text-purple-800 border-purple-200',
      unknown: 'bg-muted text-foreground border-border'
    };
    return colors[classification] || colors.unknown;
  };

  const getConfidenceColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="scanner-title">
            Transaction Scanner
          </h1>
          <p className="text-muted-foreground mt-1">Detect potential Division 7A loans from transaction data</p>
        </div>
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="scan-transaction-button">
              <Upload className="w-4 h-4 mr-2" />
              Scan Transaction
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Scan Transaction for Division 7A</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleManualScan} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="client">Client *</Label>
                  <Select value={manualEntry.client_id} onValueChange={(value) => setManualEntry({...manualEntry, client_id: value})}>
                    <SelectTrigger id="client" data-testid="transaction-client-select">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="transaction_date">Transaction Date *</Label>
                  <Input
                    id="transaction_date"
                    type="date"
                    value={manualEntry.transaction_date}
                    onChange={(e) => setManualEntry({...manualEntry, transaction_date: e.target.value})}
                    data-testid="transaction-date-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={manualEntry.amount}
                    onChange={(e) => setManualEntry({...manualEntry, amount: e.target.value})}
                    placeholder="50000.00"
                    data-testid="transaction-amount-input"
                  />
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={manualEntry.description}
                    onChange={(e) => setManualEntry({...manualEntry, description: e.target.value})}
                    placeholder="Director loan - John Smith"
                    rows={2}
                    data-testid="transaction-description-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="account_code">Account Code</Label>
                  <Input
                    id="account_code"
                    value={manualEntry.account_code}
                    onChange={(e) => setManualEntry({...manualEntry, account_code: e.target.value})}
                    placeholder="2500"
                    data-testid="account-code-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="account_name">Account Name</Label>
                  <Input
                    id="account_name"
                    value={manualEntry.account_name}
                    onChange={(e) => setManualEntry({...manualEntry, account_name: e.target.value})}
                    placeholder="Director Loan Account"
                    data-testid="account-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="transaction_type">Transaction Type *</Label>
                  <Select value={manualEntry.transaction_type} onValueChange={(value) => setManualEntry({...manualEntry, transaction_type: value})}>
                    <SelectTrigger id="transaction_type" data-testid="transaction-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="debit">Debit (Company → Individual)</SelectItem>
                      <SelectItem value="credit">Credit (Individual → Company)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="party_name">Party Name</Label>
                  <Input
                    id="party_name"
                    value={manualEntry.party_name}
                    onChange={(e) => setManualEntry({...manualEntry, party_name: e.target.value})}
                    placeholder="John Smith"
                    data-testid="party-name-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowUploadDialog(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="submit-scan-button"
                >
                  {loading ? 'Scanning...' : 'Scan Transaction'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Scanned</p>
                <p className="text-3xl font-bold text-foreground mt-2">{transactions.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Flagged for Div 7A</p>
                <p className="text-3xl font-bold text-red-900 mt-2">{transactions.filter(t => t.flagged_as_div7a).length}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Decisions Made</p>
                <p className="text-3xl font-bold text-green-900 mt-2">{transactions.filter(t => t.decision).length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Scanned Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Transactions Scanned Yet</h3>
              <p className="text-muted-foreground mb-4">Start by scanning a transaction to detect Division 7A risks</p>
              <Button onClick={() => setShowUploadDialog(true)} className="bg-primary hover:bg-primary/90">
                <Upload className="w-4 h-4 mr-2" />
                Scan Transaction
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Classification</TableHead>
                    <TableHead className="font-semibold">Confidence</TableHead>
                    <TableHead className="font-semibold">Flagged</TableHead>
                    <TableHead className="font-semibold">Decision</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id} className="hover:bg-muted/50" data-testid={`transaction-row-${transaction.id}`}>
                      <TableCell>{formatAUDate(transaction.transaction_date)}</TableCell>
                      <TableCell className="font-medium">{transaction.client_name}</TableCell>
                      <TableCell className="max-w-xs truncate">{transaction.description}</TableCell>
                      <TableCell className="font-mono">${transaction.amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell>
                        <Badge className={getClassificationColor(transaction.classification)}>
                          {transaction.classification}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`font-semibold ${getConfidenceColor(transaction.confidence_score)}`}>
                          {transaction.confidence_score}%
                        </span>
                      </TableCell>
                      <TableCell>
                        {transaction.flagged_as_div7a ? (
                          <Badge className="bg-red-100 text-red-800">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.decision ? (
                          <Badge className="bg-green-100 text-green-800">
                            {transaction.decision}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground/70">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {transaction.flagged_as_div7a && !transaction.decision && (
                            <Button
                              onClick={() => openDecisionDialog(transaction)}
                              size="sm"
                              variant="outline"
                              data-testid={`decide-${transaction.id}`}
                            >
                              Decide
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Decision Dialog */}
      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Division 7A Decision</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-6 mt-4">
              {/* Transaction Summary */}
              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Client</p>
                      <p className="font-semibold">{selectedTransaction.client_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Amount</p>
                      <p className="font-mono font-semibold">${selectedTransaction.amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Description</p>
                      <p className="font-semibold">{selectedTransaction.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Decision Options */}
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Choose an Action:</h3>
                
                <Button
                  onClick={() => handleDecision('repay')}
                  variant="outline"
                  className="w-full justify-start h-auto p-4 hover:border-green-500 hover:bg-green-50"
                  data-testid="decision-repay"
                >
                  <div className="text-left">
                    <div className="font-semibold text-green-700">Repay Before Lodgement</div>
                    <div className="text-sm text-muted-foreground">Have client repay amount before tax return lodgement</div>
                    <div className="text-xs text-green-600 mt-1">✓ No Division 7A loan required | ✓ No deemed dividend</div>
                  </div>
                </Button>
                
                <Button
                  onClick={() => handleDecision('dividend')}
                  variant="outline"
                  className="w-full justify-start h-auto p-4 hover:border-blue-500 hover:bg-blue-50"
                  data-testid="decision-dividend"
                >
                  <div className="text-left">
                    <div className="font-semibold text-blue-700">Declare as Dividend</div>
                    <div className="text-sm text-muted-foreground">Declare amount as an unfranked dividend</div>
                    <div className="text-xs text-blue-600 mt-1">⚠ Tax payable at marginal rate | ⚠ Franking credits not available</div>
                  </div>
                </Button>
                
                <Button
                  onClick={() => handleDecision('loan')}
                  variant="outline"
                  className="w-full justify-start h-auto p-4 hover:border-amber-500 hover:bg-amber-50"
                  data-testid="decision-loan"
                >
                  <div className="text-left">
                    <div className="font-semibold text-amber-700">Convert to Division 7A Loan</div>
                    <div className="text-sm text-muted-foreground">Create compliant loan agreement with MYR schedule</div>
                    <div className="text-xs text-amber-600 mt-1">✓ Compliant if MYR paid | ⚠ Interest charged at {selectedTransaction.interest_rate || '4.52'}%</div>
                  </div>
                </Button>
                
                <Button
                  onClick={() => handleDecision('ignore')}
                  variant="outline"
                  className="w-full justify-start h-auto p-4 hover:border-primary hover:bg-muted/50"
                  data-testid="decision-ignore"
                >
                  <div className="text-left">
                    <div className="font-semibold text-foreground/90">Ignore (Not Division 7A)</div>
                    <div className="text-sm text-muted-foreground">This transaction does not trigger Division 7A</div>
                    <div className="text-xs text-muted-foreground mt-1">Use for salary, reimbursements, or other excluded transactions</div>
                  </div>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}